import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy } from '@angular/router';

/**
 * Recrée le composant d'une route quand ses paramètres de chemin changent.
 *
 * La stratégie par défaut d'Angular réutilise l'instance dès que la `routeConfig`
 * est identique : passer de `/crm/personnemorale/12` à `/crm/personnemorale/34`
 * (lien vers une autre fiche) change l'URL mais garde le composant, son
 * `ngOnInit` ne rejoue pas et la vue reste celle de la fiche précédente. La
 * plupart des pages d'édition lisent `snapshot.params` une seule fois, et même
 * celles qui s'abonnent à `params` gardent l'état des sections déjà montées.
 *
 * Les query params ne sont pas concernés (onglets `?tab=`, filtres) : seule
 * une valeur de `params` différente force la recréation.
 *
 * Une route qui gère elle-même le changement de paramètre sans vouloir perdre
 * son état (liste + détail, streaming…) le déclare avec
 * `data: { reuseOnParamChange: true }`.
 */
@Injectable()
export class ParamAwareRouteReuseStrategy extends BaseRouteReuseStrategy {
    override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        if (future.routeConfig !== curr.routeConfig) {
            return false;
        }
        if (future.routeConfig?.data?.['reuseOnParamChange']) {
            return true;
        }
        return memesParams(future.params, curr.params);
    }
}

function memesParams(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
    const clesA = Object.keys(a);
    if (clesA.length !== Object.keys(b).length) {
        return false;
    }
    return clesA.every(cle => a[cle] === b[cle]);
}
